
import { db, auth } from '../../src/firebase';
import { collection, doc, getDoc, setDoc, updateDoc, deleteDoc, onSnapshot, getDocs, limit, query, where, Timestamp } from 'firebase/firestore';
import { User } from 'firebase/auth';
import * as encryption from '../security/encryption';

/**
 * CloudSyncService handles the mirroring of local IndexedDB data to Firestore.
 * It uses the organization (admin) UID as the root path.
 */
export class CloudSyncService {
    private static instance: CloudSyncService;
    private user: User | null = null;
    private orgUid: string | null = null;
    private encryptionSalt: string | null = null;
    private userRole: string | null = null;
    private currentUserId: string | null = null;
    private activeDEK: CryptoKey | null = null;
    private wrappedDEK: string | null = null;
    private preloadedPassphrase: string | null = null;
    private hasPersonalKeyFlag: boolean = false;
    private memberDocId: string | null = null; // The actual document ID in the 'members' collection

    private constructor() { }

    public static getInstance(): CloudSyncService {
        if (!CloudSyncService.instance) {
            CloudSyncService.instance = new CloudSyncService();
        }
        return CloudSyncService.instance;
    }

    public async setUser(user: User | null, orgUid?: string, role?: string | null) {
        this.user = user;
        // FIX: Do NOT default to user.uid here. Wait for setTenantId to set the correct Owner UID.
        // This prevents drivers from trying to sync with their own non-existent org path.
        if (orgUid !== undefined) {
            this.orgUid = orgUid;
        } else if (!user) {
            this.orgUid = null;
        }

        this.userRole = role || null;
        this.currentUserId = user?.uid || null;

        if (this.orgUid) {
            await this.initializeSecurity();
        } else {
            this.encryptionSalt = null;
        }
    }

    public async setRole(role: string | null) {
        this.userRole = role;
    }

    public async setTenantId(orgUid: string | null) {
        this.orgUid = orgUid;
        if (this.orgUid) {
            await this.initializeSecurity();
        } else {
            this.encryptionSalt = null;
        }
    }

    public setPreloadedPassphrase(passphrase: string | null) {
        this.preloadedPassphrase = passphrase;
    }

    public get isEncryptionConfigured(): boolean {
        return !!this.encryptionSalt && !!this.wrappedDEK;
    }

    public get hasActiveDEK(): boolean {
        return !!this.activeDEK;
    }

    private async initializeSecurity() {
        if (!this.orgUid) return;

        // 1. Get Org-wide security settings (Salt & potentially Org-wide wrapped DEK)
        const securityRef = doc(db, 'organizations', this.orgUid, 'settings', 'security');
        const snap = await getDoc(securityRef);

        if (snap.exists() && snap.data().salt) {
            this.encryptionSalt = snap.data().salt;
            this.wrappedDEK = snap.data().wrappedDEK || null;
            this.hasPersonalKeyFlag = false;
        } else {
            // New Organization: Generate a random 16-byte hex salt
            const randomBytes = crypto.getRandomValues(new Uint8Array(16));
            const newSalt = Array.from(randomBytes).map(b => b.toString(16).padStart(2, '0')).join('');

            await setDoc(securityRef, {
                salt: newSalt,
                updatedAt: new Date().toISOString()
            }, { merge: true });

            this.encryptionSalt = newSalt;
        }

        // 2. For non-admins (or users with personal keys), prioritize their specific wrapped key
        if (this.currentUserId) {
            let memberData: any = null;
            let foundDocId: string | null = null;

            // Try direct lookup (fastest if ID matches)
            const memberRef = doc(db, 'organizations', this.orgUid, 'members', this.currentUserId);
            const mSnap = await getDoc(memberRef);

            if (mSnap.exists()) {
                memberData = mSnap.data();
                foundDocId = mSnap.id;
            } else {
                // Try query by 'firebaseUid' field if ID doesn't match
                const membersRef = collection(db, 'organizations', this.orgUid, 'members');
                let qSnap = await getDocs(query(membersRef, where('firebaseUid', '==', this.currentUserId), limit(1)));

                // Fallback: Try by Email if not found by firebaseUid
                if (qSnap.empty && this.user?.email) {
                    qSnap = await getDocs(query(membersRef, where('email', '==', this.user.email), limit(1)));
                }

                if (!qSnap.empty) {
                    memberData = qSnap.docs[0].data();
                    foundDocId = qSnap.docs[0].id;
                }
            }

            if (foundDocId) {
                this.memberDocId = foundDocId;
            }

            if (memberData?.wrappedDEK) {
                this.wrappedDEK = memberData.wrappedDEK;
                this.hasPersonalKeyFlag = true;
                console.log(`[CloudSync] Personal wrapped DEK found for user ${this.currentUserId}`);
            } else {
                this.hasPersonalKeyFlag = false;
            }
        }


        if (!this.wrappedDEK) {
            console.warn('[CloudSync] No wrapped DEK found for this user or organization.');
        }

        // 3. Try Auto-unlock from Local Storage (Web)
        await this.tryAutoUnlock();

        // 4. Try Auto-unlock with preloaded passphrase (from Login)
        if (!this.activeDEK && this.preloadedPassphrase && this.wrappedDEK && this.encryptionSalt) {
            try {
                await this.unlockEncryption(this.preloadedPassphrase);
                console.log('[CloudSync] DEK auto-unlocked using preloaded passphrase.');
                // We intentionally leave preloadedPassphrase in memory 
                // so ForcePasswordChange can use it directly without asking the user again.
            } catch (e) {
                console.warn('[CloudSync] Preloaded passphrase failed to unlock DEK.');
            }
        }
    }

    private async tryAutoUnlock() {
        if (!this.orgUid) return;
        const storageKey = `dek_${this.orgUid}`;
        const storedRaw = localStorage.getItem(storageKey);
        if (storedRaw) {
            try {
                const raw = encryption.base64ToUint8Array(storedRaw);
                this.activeDEK = await encryption.importKey(raw);
                console.log('[CloudSync] DEK auto-unlocked from storage.');
            } catch (e) {
                console.warn('[CloudSync] Failed to auto-unlock DEK:', e);
                localStorage.removeItem(storageKey);
            }
        }
    }

    /**
     * Initializes a new organization's encryption with a random DEK and admin passphrase.
     */
    public async setupEncryption(passphrase: string) {
        if (!this.orgUid || !this.encryptionSalt) throw new Error('Security not initialized');

        // Generate Master DEK
        const dek = await encryption.generateDEK();
        const wrapped = await encryption.wrapKey(dek, passphrase, this.encryptionSalt);

        // Save wrapped DEK to the organization settings
        const securityRef = doc(db, 'organizations', this.orgUid, 'settings', 'security');
        await updateDoc(securityRef, {
            wrappedDEK: wrapped,
            updatedAt: new Date().toISOString()
        });

        this.activeDEK = dek;
        this.wrappedDEK = wrapped;
        this.persistDEK(dek);
    }

    /**
     * Unlocks the DEK using a user-provided passphrase.
     */
    public async unlockEncryption(passphrase: string): Promise<boolean> {
        if (!this.orgUid || !this.encryptionSalt || !this.wrappedDEK) {
            console.warn('[CloudSync] Unlock failed: Security not initialized or no wrapped key found.');
            return false;
        }

        try {
            const dek = await encryption.unwrapKey(this.wrappedDEK, passphrase, this.encryptionSalt);
            this.activeDEK = dek;
            this.persistDEK(dek);
            return true;
        } catch (e) {
            console.error('[CloudSync] Invalid passphrase or corrupted key.');
            return false;
        }
    }

    private async persistDEK(key: CryptoKey) {
        if (!this.orgUid) return;
        const raw = await encryption.exportKey(key);
        const base64 = encryption.arrayBufferToBase64(raw);
        localStorage.setItem(`dek_${this.orgUid}`, base64);
    }

    public isEncryptionLocked(): boolean {
        return !!this.wrappedDEK && !this.activeDEK;
    }

    public hasEncryptionSetup(): boolean {
        return !!this.wrappedDEK;
    }

    public hasPersonalKey(): boolean {
        return this.hasPersonalKeyFlag;
    }

    public getPreloadedPassphrase(): string | null {
        return this.preloadedPassphrase;
    }

    public async issuePersonalKey(loginPassword: string): Promise<boolean> {
        // Use memberDocId if available, fallback to currentUserId
        const targetMemberId = this.memberDocId || this.currentUserId;
        if (!this.activeDEK || !this.encryptionSalt || !this.orgUid || !targetMemberId) return false;
        try {
            const newWrappedDEK = await encryption.wrapKey(this.activeDEK, loginPassword, this.encryptionSalt);
            const memberRef = doc(db, 'organizations', this.orgUid, 'members', targetMemberId);
            // using merge: true to just update wrappedDEK
            await setDoc(memberRef, { wrappedDEK: newWrappedDEK }, { merge: true });

            this.wrappedDEK = newWrappedDEK;
            this.hasPersonalKeyFlag = true;
            console.log('[CloudSync] Auto-issued personal key using login password.');
            return true;
        } catch (e) {
            console.error('[CloudSync] Failed to issue personal key:', e);
            return false;
        }
    }

    /**
     * Returns parameters for encryption: salt and a derivation password.
     * The password is tied to the organization's unique ID.
     */
    public async getEncryptionParams(): Promise<{ salt: string; password: string; key?: CryptoKey } | null> {
        if (!this.orgUid) return null;
        if (!this.encryptionSalt) {
            await this.initializeSecurity();
        }
        if (!this.encryptionSalt) return null;

        // NEW FLOW: Prefer explicit CryptoKey (DEK)
        if (this.activeDEK) {
            return {
                salt: this.encryptionSalt,
                password: '', // Password unused when key is present
                key: this.activeDEK
            };
        }

        // FALLBACK: Old PBKDF2 flow
        return {
            salt: this.encryptionSalt,
            password: this.orgUid // Org UID acts as a stable, unique password for PBKDF2
        };
    }

    private getDocPath(collectionName: string, id: string) {
        if (!this.orgUid) return null;
        return doc(db, 'organizations', this.orgUid, collectionName, id);
    }

    private getCollectionPath(collectionName: string) {
        if (!this.orgUid) return null;
        return collection(db, 'organizations', this.orgUid, collectionName);
    }

    public async syncCreate(collectionName: string, id: string, data: any) {
        if (!id) {
            console.error(`[CloudSync] SyncCreate: MISSING ID for ${collectionName}`);
            return;
        }

        const docRef = this.getDocPath(collectionName, id);
        if (!docRef) return;

        // ROLE-BASED WRITE PROTECTION
        if (this.userRole === 'driver' && this.currentUserId) {
            const isOwnData = this.checkOwnership(collectionName, data);
            if (!isOwnData) {
                console.warn(`[CloudSync] BLOCKED syncCreate for ${collectionName}/${id}: Role is driver and data ownership check failed.`);
                return;
            }
        }

        // DEBUG LOGGING - TEMPORARILY ENABLED FOR DIAGNOSTICS
        console.log(`[CloudSync] Syncing ${collectionName}/${id} to ${docRef.path}`);

        try {
            await setDoc(docRef, {
                ...data,
                _updatedAt: new Date().toISOString(),
                _createdBy: this.user?.uid || 'anon'
            });
            console.log(`[CloudSync] SUCCESS synced ${collectionName}/${id}`);
        } catch (e) {
            console.error(`[CloudSync] FAILED to sync ${collectionName}/${id}`, e);
        }
    }

    public async syncUpdate(collectionName: string, id: string, patch: any) {
        const docRef = this.getDocPath(collectionName, id);
        if (!docRef) return;

        try {
            await updateDoc(docRef, {
                ...patch,
                _updatedAt: new Date().toISOString()
            });
            console.log(`[CloudSync] SUCCESS updated ${collectionName}/${id}`);
        } catch (e) {
            console.error(`[CloudSync] FAILED to update ${collectionName}/${id}`, e);
        }
    }

    public async syncDelete(collectionName: string, id: string) {
        const docRef = this.getDocPath(collectionName, id);
        if (!docRef) return;

        await deleteDoc(docRef);
    }

    /**
     * Checks if the current user (driver) owns the data being synced.
     */
    private checkOwnership(collectionName: string, data: any): boolean {
        if (!this.currentUserId) return false;

        switch (collectionName) {
            case 'employees':
                return data.firebaseUid === this.currentUserId || data.id === this.currentUserId;
            case 'waybills':
                return data.driverId === this.currentUserId || data.assignedDriverUid === this.currentUserId;
            case 'vehicles':
                // Drivers can update their assigned vehicle (e.g. mileage)
                return data.assignedDriverId === this.currentUserId ||
                    (Array.isArray(data.assignedDriverUids) && data.assignedDriverUids.includes(this.currentUserId));
            case 'refuelings':
                return data.driverId === this.currentUserId;
            default:
                // Other collections (organizations, fuelTypes, etc) are strictly admin-only for writes
                return false;
        }
    }

    public async syncMember(userId: string, role: 'admin' | 'dispatcher' | 'driver', accessibleOrgIds: string[], email?: string | null, wrappedDEK?: string) {
        if (!this.orgUid) return;
        const memberRef = doc(db, 'organizations', this.orgUid, 'members', userId);
        await setDoc(memberRef, {
            uid: userId,
            role,
            accessibleOrgIds,
            email: email || null,
            wrappedDEK: wrappedDEK || null,
            updatedAt: new Date().toISOString()
        }, { merge: true });
    }

    /**
     * Batch sync for migration.
     */
    public async syncAll(collectionName: string, items: any[]) {
        if (!this.orgUid || items.length === 0) return;

        console.log(`[CloudSync] Batch syncing ${items.length} items to ${this.orgUid}/${collectionName}...`);

        // Firestore batch has a limit of 500, but we'll just use parallel writes for simplicity in this MVP
        // or a real writeBatch if needed.
        const promises = items.map(item => this.syncCreate(collectionName, item.id, item));
        await Promise.all(promises);
    }

    /**
     * One-way sync from Firestore to local storage for a specific entity.
     * This is useful for drivers or managers who need to see data from other sources.
     */
    public subscribeToCollection(collectionName: string, callback: (items: any[]) => void) {
        const colRef = this.getCollectionPath(collectionName);
        if (!colRef) return () => { };

        const q = query(colRef);
        return onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            callback(items);
        });
    }

    /**
     * Fetches all documents from a cloud collection.
     * Used for manual restore/sync-down.
     */
    public async restoreCollection(collectionName: string): Promise<any[]> {
        const colRef = this.getCollectionPath(collectionName);
        if (!colRef) {
            console.warn(`[CloudSync] Cannot restore ${collectionName}: No orgUid set.`);
            return [];
        }

        console.log(`[CloudSync] Restoring ${collectionName} from path: ${colRef.path} (orgUid: ${this.orgUid}, role: ${this.userRole}, currentUserId: ${this.currentUserId})`);

        try {
            let q = query(colRef);

            // ROLE-BASED READ FILTERING
            if (this.userRole === 'driver' && this.currentUserId) {
                if (collectionName === 'waybills') {
                    console.log(`[CloudSync] 🔍 Start Waybill Restore. User Auth UID (currentUserId): ${this.currentUserId}`);
                    // 1. By Auth UID (New logic)
                    const snapshotByUid = await getDocs(query(colRef, where('assignedDriverUid', '==', this.currentUserId)));
                    console.log(`[CloudSync] 🔍 Waybills finding by assignedDriverUid=${this.currentUserId} -> Found: ${snapshotByUid.size}`);

                    // 2. By Employee ID (Legacy logic)
                    let myEmployeeId: string | null = null;
                    const employeesRef = this.getCollectionPath('employees');
                    if (employeesRef) {
                        let selfSnap = await getDocs(query(employeesRef, where('firebaseUid', '==', this.currentUserId)));
                        if (selfSnap.empty && this.user?.email) {
                            selfSnap = await getDocs(query(employeesRef, where('email', '==', this.user.email)));
                        }
                        if (!selfSnap.empty) {
                            myEmployeeId = selfSnap.docs[0].id;
                        }
                    }

                    let snapshotByLegacy: any = { docs: [] };
                    if (myEmployeeId) {
                        console.log(`[CloudSync] 🔍 Found Employee ID for this user: ${myEmployeeId}. Querying waybills by driverId...`);
                        snapshotByLegacy = await getDocs(query(colRef, where('driverId', '==', myEmployeeId)));
                        console.log(`[CloudSync] 🔍 Waybills finding by driverId=${myEmployeeId} -> Found: ${snapshotByLegacy.size}`);
                    } else {
                        console.warn(`[CloudSync] ⚠️ Could not find Employee ID for user ${this.currentUserId} (${this.user?.email})`);
                    }

                    const items = [
                        ...snapshotByUid.docs.map(doc => ({ id: doc.id, ...doc.data() })),
                        ...snapshotByLegacy.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                    ];

                    const uniqueItems = Array.from(new Map(items.map(item => [item.id, item])).values());
                    console.log(`[CloudSync] Restore waybills: found ${uniqueItems.length} docs (UID + Legacy).`);
                    return uniqueItems;

                } else if (collectionName === 'refuelings') {
                    q = query(colRef, where('driverId', '==', this.currentUserId));
                } else if (collectionName === 'employees') {
                    // Drivers see themselves AND responsible persons
                    const snapshotStaff = await getDocs(query(colRef, where('employeeType', 'in', ['dispatcher', 'mechanic', 'controller', 'manager', 'accountant'])));

                    // Try to find SELF by firebaseUid
                    let snapshotSelf = await getDocs(query(colRef, where('firebaseUid', '==', this.currentUserId)));
                    console.log(`[CloudSync] Employee lookup by UID (${this.currentUserId}): ${snapshotSelf.size} docs`);

                    // Fallback: Try by Email if not found by UID
                    if (snapshotSelf.empty && this.user?.email) {
                        console.warn('[CloudSync] Self not found by UID, trying email:', this.user.email);
                        snapshotSelf = await getDocs(query(colRef, where('email', '==', this.user.email)));
                        console.log(`[CloudSync] Employee lookup by Email (${this.user.email}): ${snapshotSelf.size} docs`);
                    }

                    const items = [
                        ...snapshotStaff.docs.map(doc => ({ id: doc.id, ...doc.data() })),
                        ...snapshotSelf.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                    ];
                    // Deduplicate
                    const uniqueItems = Array.from(new Map(items.map(item => [item.id, item])).values());
                    console.log(`[CloudSync] Restore employees (driver mode): found ${uniqueItems.length} docs (staff + self).`);
                    return uniqueItems;
                } else if (collectionName === 'vehicles') {
                    // 1. Fetch by Modern Array (Firebase UIDs)
                    const snapshotByUid = await getDocs(query(colRef, where('assignedDriverUids', 'array-contains', this.currentUserId)));

                    // 2. Fetch by Legacy ID (Employee UUID)
                    // We need to find OUR employee ID first.
                    let myEmployeeId: string | null = null;
                    const employeesRef = this.getCollectionPath('employees');
                    if (employeesRef) {
                        let selfSnap = await getDocs(query(employeesRef, where('firebaseUid', '==', this.currentUserId)));
                        if (selfSnap.empty && this.user?.email) {
                            selfSnap = await getDocs(query(employeesRef, where('email', '==', this.user.email)));
                        }
                        if (!selfSnap.empty) {
                            myEmployeeId = selfSnap.docs[0].id;
                        }
                    }

                    let snapshotByLegacy: any = { docs: [] };
                    if (myEmployeeId) {
                        snapshotByLegacy = await getDocs(query(colRef, where('assignedDriverId', '==', myEmployeeId)));
                    }

                    const items = [
                        ...snapshotByUid.docs.map(doc => ({ id: doc.id, ...doc.data() })),
                        ...snapshotByLegacy.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                    ];

                    const uniqueItems = Array.from(new Map(items.map(item => [item.id, item])).values());
                    console.log(`[CloudSync] Restore vehicles: found ${uniqueItems.length} docs (UID + Legacy).`);
                    return uniqueItems;

                } else if (['organizations', 'fuelTypes', 'storages', 'data_backup'].includes(collectionName)) {
                    // Shared dictionaries: FULL SYNC
                    q = query(colRef);
                } else {
                    // Default for drivers for unknown/unhandled collections: restrict to self created if possible, or empty
                    q = query(colRef, where('_createdBy', '==', this.currentUserId));
                }
            }

            const snapshot = await getDocs(q);
            console.log(`[CloudSync] Restore ${collectionName}: found ${snapshot.size} docs.`);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (e) {
            console.error(`[CloudSync] Failed to restore ${collectionName}:`, e);
            throw e;
        }
    }
}

export const cloudSync = CloudSyncService.getInstance();
